import { useCallback } from "react"
import { isAxiosError } from "axios"
import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  getProjectVersion,
  compareDatamaps,
  recordDeliverable,
} from "api/Engine/projectVersion"
import { compareTrackerMetadata } from "api/Repos/trackers"
import { notify } from "shared/ui/Notifications/notify"
import { useProjectStore } from "stores/projectStore"
import { ProjectVersion, RecordDeliverableResult } from "types/projectVersion.types"
import { AuditChange } from "types/questionnaire.types"
import { sha256OfBlob } from "utils/files/downloadUtils"
import { failureOf } from "utils/errMsgOf"
import { ProjectRef, useProjectOwner } from "./useProjectOwner"

/** What an export is recorded with, beside the file it wrote. */
export interface DeliverableDetails {
  /** The exported file's name, without extension */
  filename: string
  format: string
  /** The applied export map's text (it may be an unsaved edit) */
  exportMap?: string
  /** The exporting session, so the server can capture its lineage */
  instanceId?: string
}

/** One recorded export: the version read before it, and the file it wrote. */
interface DeliverableRecord extends DeliverableDetails {
  /** The version read before the export, or why it couldn't be read */
  before: { projectVersion: string } | { error: unknown }
  file: File
}

/**
 * The words for an export that couldn't be recorded. A 409 for the project changing during the
 * export clears on a fresh export, so it says to export again; the server's own 409 for a session
 * holding replaced data already says what to do (reopen the data first), so nothing is added.
 */
const notRecordedMessage = (error: unknown): string => {
  const message = failureOf(error).trim().replace(/\.$/, "")
  const saysWhatToDo = /export again/i.test(message)
  const again =
    isAxiosError(error) && error.response?.status === 409 && !saysWhatToDo ? " Export again to record it." : ""
  return `The file was exported, but it is not in the project history. ${message}.${again}`
}

/**
 * Records exports of the open project in its history. `exportAndRecord` reads the version ID,
 * runs the export, and then records the file it wrote: its SHA-256 and size, with the version
 * read before, so the server refuses the record when the project changed during the export. A
 * successful export is always recorded; one that couldn't be is reported, and the file stays
 * exported. The history reads are refreshed after a record.
 */
export const useRecordDeliverable = () => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()
  const projectId = useProjectStore((s) => s.project.id)
  const owner = useProjectOwner()

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      before,
      file,
      filename,
      format,
      exportMap,
      instanceId,
    }: DeliverableRecord): Promise<RecordDeliverableResult> => {
      if ("error" in before) throw before.error
      const artifact = {
        sha256: await sha256OfBlob(file),
        sizeBytes: file.size,
        filename: file.name,
      }
      const res = await recordDeliverable(
        await getAxios(),
        projectId,
        owner,
        filename,
        format,
        { expectedProjectVersion: before.projectVersion, exportMap, artifact },
        instanceId
      )
      return res.data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.provenance.project(projectId) }),
    onError: (error) => notify.error("Export not recorded", notRecordedMessage(error)),
  })

  /**
   * Runs `run` (an export that resolves to the file it wrote, or null when it failed) and
   * records that file. Resolves to whether the file was exported, and the record when it was
   * recorded.
   */
  const exportAndRecord = useCallback(
    async (
      details: DeliverableDetails,
      run: () => Promise<File | null>
    ): Promise<{ exported: boolean; recorded: RecordDeliverableResult | null }> => {
      let before: DeliverableRecord["before"]
      try {
        before = {
          projectVersion: (await getProjectVersion(await getAxios(), projectId, owner)).data
            .projectVersion,
        }
      } catch (error) {
        before = { error }
      }
      const file = await run()
      if (!file) return { exported: false, recorded: null }
      try {
        return { exported: true, recorded: await mutateAsync({ ...details, before, file }) }
      } catch {
        // Reported by the mutation
        return { exported: true, recorded: null }
      }
    },
    [getAxios, projectId, owner, mutateAsync]
  )

  return { exportAndRecord }
}

/** Another wave compared with the open project. */
export interface WaveComparison {
  /** The open project's stamp. */
  version: ProjectVersion
  /** The other wave's stamp. */
  otherVersion: ProjectVersion
  /** What changed from the other wave to the open project; null where the two are the same. */
  qnrChanges: AuditChange[] | null
  datamapChanges: AuditChange[] | null
  /**
   * How each comparison went, where one was made: "ok", or why nothing was compared ("no_qnr",
   * "no_data", "parse_failed"). Only "ok" lists changes. A read that failed fails the query.
   */
  qnrStatus?: string
  datamapStatus?: string
}

/**
 * Compares another wave (`other`, with its own owner) with the open project: both stamps, and the
 * questionnaire and datamap changes where those differ. The other wave is the base, so "added"
 * reads as new in the open project. Reads nothing until a wave is picked. Silent: the comparison
 * says inline when it failed.
 */
export const useCompareQuery = (
  other: ProjectRef | undefined,
  projectId: string
): UseQueryResult<WaveComparison> => {
  const getAxios = useAuthedAxios()
  const owner = useProjectOwner()
  return useQuery({
    queryKey: queryKeys.provenance.compare(projectId, owner, other?.id ?? "", other?.owner ?? ""),
    enabled: !!projectId && !!owner && !!other?.id,
    meta: { silent: true },
    queryFn: async (): Promise<WaveComparison> => {
      const base = other as ProjectRef
      const axios = await getAxios()
      const [mine, theirs] = await Promise.all([
        getProjectVersion(axios, projectId, owner),
        getProjectVersion(axios, base.id, base.owner),
      ])
      const version = mine.data
      const otherVersion = theirs.data
      // A reply without a stamp (an error body, an older server) can't be compared
      if (!version?.components || !otherVersion?.components)
        throw new Error("Couldn't read the version ID of one of the projects.")
      const [qnr, datamap] = await Promise.all([
        version.components.qnr === otherVersion.components.qnr
          ? null
          : compareTrackerMetadata(axios, base.id, base.owner, projectId, owner).then((res) => ({
              changes: res.data?.changes ?? [],
              status: res.data?.parseStatus,
            })),
        version.components.datamap === otherVersion.components.datamap
          ? null
          : compareDatamaps(axios, base.id, base.owner, projectId, owner).then((res) => ({
              changes: res.data?.changes ?? [],
              status: res.data?.parseStatus,
            })),
      ])
      return {
        version,
        otherVersion,
        qnrChanges: qnr ? qnr.changes : null,
        datamapChanges: datamap ? datamap.changes : null,
        qnrStatus: qnr?.status,
        datamapStatus: datamap?.status,
      }
    },
  })
}
